import React, { useState, useRef, useCallback } from "react";
import { useUserContext } from "../config/UserContext";
import { useKeycloak } from "../config/KeycloakContext";
import { DOCUMENT_TYPE } from "../../../shared/types/models/document";
import type { Parcel } from "../../../shared/types/models/parcel";
import { TrashIcon, PlusIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { useLoadScript } from "@react-google-maps/api";

const libraries: any = ['places'];

function useNewPlacesAutocomplete(selectedCountry: string) {
    const [value, setValue] = useState('');
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const sessionTokenRef = useRef<any>(null);

    const initToken = () => {
        if (!sessionTokenRef.current && window.google?.maps?.places?.AutocompleteSessionToken) {
            sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        }
    };

    const fetchSuggestions = useCallback(async (input: string) => {
        setValue(input);
        if (!input) {
            setSuggestions([]);
            return;
        }

        if (!window.google?.maps?.places?.AutocompleteSuggestion) {
            return;
        }

        initToken();
        setLoading(true);

        try {
            const request: any = {
                input,
                sessionToken: sessionTokenRef.current,
            };
            
            if (selectedCountry) {
                request.includedRegionCodes = [selectedCountry.toLowerCase()];
            }

            const response = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
            setSuggestions(response.suggestions || []);
        } catch (error) {
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    }, [selectedCountry]);

    const clearSuggestions = useCallback(() => {
        setSuggestions([]);
    }, []);

    const fetchPlaceDetails = async (placeId: string): Promise<any> => {
        // Use the new Place API (google.maps.places.Place) instead of deprecated PlacesService
        if (window.google?.maps?.places?.Place) {
            const place = new window.google.maps.places.Place({ id: placeId });
            await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });
            // Convert the new API response shape to match the old PlacesService shape
            const addressComponents = place.addressComponents?.map((component: any) => ({
                long_name: component.longText || component.long_name || '',
                short_name: component.shortText || component.short_name || '',
                types: component.types || [],
            })) || [];
            return {
                formatted_address: place.formattedAddress || '',
                address_components: addressComponents,
            };
        }
        // Fallback to legacy PlacesService if new API not available
        return new Promise((resolve, reject) => {
            if (!window.google?.maps?.places?.PlacesService) {
                reject(new Error("PlacesService not loaded"));
                return;
            }
            const div = document.createElement('div');
            div.style.display = 'none';
            document.body.appendChild(div);
            const service = new window.google.maps.places.PlacesService(div);
            service.getDetails({
                placeId,
                fields: ['address_components', 'formatted_address']
            }, (place, status) => {
                document.body.removeChild(div);
                if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    resolve(place);
                } else {
                    reject(new Error(status));
                }
            });
        });
    };

    return { value, setValue, suggestions, fetchSuggestions, clearSuggestions, fetchPlaceDetails, loading };
}

interface ParcelCollectionCardProps {
  config: any;
  freezeLookup?: string;
  onBack?: () => void;
  onContinue: () => void;
}

const ParcelCollectionCard: React.FC<ParcelCollectionCardProps> = ({ config, freezeLookup, onBack, onContinue }) => {
  const { document, freeze, parcels, setParcels, apiBaseUrl, county } = useUserContext();
  const { getToken } = useKeycloak();
  const [localParcels, setLocalParcels] = useState<Partial<Parcel>[]>(parcels && parcels.length > 0 ? parcels : [{}]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<number, Record<string, string>>>({});
  const [activeAddressIndex, setActiveAddressIndex] = useState<number | null>(null);

  const { isLoaded } = useLoadScript({
      googleMapsApiKey: import.meta.env.VITE_GOOGLE_PLACES_API_KEY || '',
      libraries,
      version: "weekly"
  });

  const {
      suggestions: addressSuggestions,
      fetchSuggestions: fetchAddressSuggestions,
      clearSuggestions: clearAddressSuggestions,
      fetchPlaceDetails
  } = useNewPlacesAutocomplete('US');

  const handleSelectSuggestion = async (suggestion: any, index: number) => {
    const predictionText = (suggestion?.placePrediction?.text?.text || suggestion?.description || '')
      .replace(/,\s*USA$/, '')
      .replace(/\s*USA$/, '')
      .trim();

    clearAddressSuggestions();
    setActiveAddressIndex(null);
    if (predictionText) {
      handleChange(index, 'address', predictionText);
      handleChange(index, 'street1', predictionText);
    }

    try {
        const placeId = suggestion.placePrediction?.placeId || suggestion.place_id;
        if (!placeId) throw new Error("No place_id");
        
        const place = await fetchPlaceDetails(placeId);
        if (place && place.address_components) {
            let locality = '';
            let admin_area = '';
            let zip = '';

            place.address_components.forEach((c: any) => {
                if (c.types.includes('locality') || c.types.includes('sublocality') || c.types.includes('neighborhood')) locality = c.long_name;
                if (c.types.includes('administrative_area_level_1')) admin_area = c.short_name;
                if (c.types.includes('postal_code')) zip = c.long_name;
            });

            const apiFormatted = (place.formatted_address || '')
              .replace(/,\s*USA$/, '')
              .replace(/\s*USA$/, '')
              .trim();
            let formatted = (apiFormatted && apiFormatted.includes(',') && apiFormatted.length >= predictionText.length)
              ? apiFormatted
              : (predictionText || apiFormatted);
            if (zip && !formatted.includes(zip)) {
              formatted = `${formatted} ${zip}`;
            }
            
            handleChange(index, 'address', formatted);
            handleChange(index, 'street1', formatted);
            if (locality) handleChange(index, 'city', locality);
            if (admin_area) handleChange(index, 'state', admin_area);
            if (zip) handleChange(index, 'zipcode', zip);
        } else {
            throw new Error("No details");
        }
    } catch (e) {
        if (predictionText) {
          handleChange(index, 'address', predictionText);
          handleChange(index, 'street1', predictionText);
        }
    }
  };

  const isLienRelease = document?.type !== DOCUMENT_TYPE.LIEN;
  
  const parcelSchema = config || {
    requiresAddress: true,
    requiresLegalDescription: false,
    requiresAbbreviatedLegalDescription: false,
    parcelIndexes: []
  };

  const handleAddParcel = () => {
    setLocalParcels(prev => [...prev, {}]);
  };

  const handleRemoveParcel = (index: number) => {
    setLocalParcels(prev => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.length > 0 ? updated : [{}];
    });
    
    const newErrors = { ...errors };
    delete newErrors[index];
    setErrors(newErrors);
  };

  const handleChange = (index: number, key: string, value: any) => {
    const standardFields = ['parcelNumber', 'legalDescription', 'abbreviatedLegalDescription', 'address', 'street1', 'street2', 'city', 'zipcode', 'countyIndexId'];
    setLocalParcels(prev => {
      const updated = [...prev];
      const current = { ...(updated[index] || {}) };
      if (standardFields.includes(key)) {
        (current as any)[key] = value;
      } else {
        current.recordingIndexes = {
          ...(current.recordingIndexes || {}),
          [key]: value
        };
      }
      updated[index] = current;
      return updated;
    });
  };

  const getValue = (index: number, key: string) => {
    const parcel = localParcels[index];
    const standardFields = ['parcelNumber', 'legalDescription', 'abbreviatedLegalDescription', 'address', 'street1', 'street2', 'city', 'zipcode', 'countyIndexId'];
    if (standardFields.includes(key)) {
      return (parcel as any)[key] || '';
    }
    return parcel.recordingIndexes?.[key] || '';
  };

  const validate = () => {
    let isValid = true;
    const newErrors: Record<number, Record<string, string>> = {};

    localParcels.forEach((parcel, index) => {
      const parcelErrors: Record<string, string> = {};
      
      if (parcelSchema.requiresAddress) {
        if (!parcel.street1 || String(parcel.street1).trim() === '') parcelErrors.street1 = 'Address is required';
      }

      if (parcelSchema.requiresLegalDescription) {
        if (!parcel.legalDescription || String(parcel.legalDescription).trim() === '') {
          parcelErrors.legalDescription = 'Legal Description is required';
        }
      }

      // Validate parcelNumber if any parcelIndex has key 'parcelNumber' OR if config explicitly requires it
      if (parcelSchema.requiresParcelNumber) {
        if (!parcel.parcelNumber || String(parcel.parcelNumber).trim() === '') {
          parcelErrors.parcelNumber = 'Parcel Number is required';
        }
      }

      if (parcelSchema.requiresAbbreviatedLegalDescription) {
        if (!parcel.abbreviatedLegalDescription || String(parcel.abbreviatedLegalDescription).trim() === '') {
          parcelErrors.abbreviatedLegalDescription = 'Abbreviated Legal Description is required';
        }
      }

      if (parcelSchema.parcelIndexes) {
        parcelSchema.parcelIndexes.forEach((idx: any) => {
          const isReq = idx.required !== false && String(idx.required).toLowerCase() !== "false" && idx.required !== 0;
          if (isReq) {
            const val = getValue(index, idx.key);
            if (!val || String(val).trim() === '') {
              parcelErrors[`index_${idx.key}`] = `${idx.label} is required`;
            }
          }
        });
      }

      if (Object.keys(parcelErrors).length > 0) {
        newErrors[index] = parcelErrors;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (isLienRelease) {
      onContinue();
      return;
    }
    
    if (!validate()) return;
    
    // Filter out completely empty "phantom" parcels
    const filteredParcels = localParcels.filter(parcel => {
      const hasStandardValues = ['parcelNumber', 'legalDescription', 'abbreviatedLegalDescription', 'street1'].some(k => !!(parcel as any)[k]);
      const hasIndexValues = parcel.recordingIndexes ? Object.values(parcel.recordingIndexes).some(v => !!v) : false;
      return hasStandardValues || hasIndexValues || parcel.countyIndexId;
    });
    
    if (filteredParcels.length === 0 && parcelSchema.allowMultipleParcels !== false) {
      // If all parcels were empty but they didn't explicitly forbid empty, just continue? No, they probably want to save empty array.
      // But if it requires things, validate() would have caught it. 
      // If validation passed on empty parcels, it means nothing was required.
    }
    
    setSaving(true);
    try {
      const token = await getToken();
      if (!freezeLookup) throw new Error("Freeze not found");
      
      const res = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${freezeLookup}/parcels`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ parcels: filteredParcels })
      });
      if (!res.ok) {
        throw new Error("Failed to save parcels");
      }
      const data = await res.json();
      setParcels(data.data);
      onContinue();
    } catch (err) {
      console.error(err);
      alert("Failed to save parcels");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full flex flex-col gap-6">
      {config?.instructionsHtml && (
        <div 
          className="text-[15px] font-plus-jakarta text-gray-700 bg-gray-50 p-4 rounded-lg border border-gray-200"
          dangerouslySetInnerHTML={{ __html: config.instructionsHtml }}
        />
      )}

      {localParcels.map((parcel, index) => (
        <div key={index} className="bg-white border border-[#D7DFEC] rounded-xl p-6 relative shadow-sm">
          <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-3">
            <h3 className="text-lg font-bold text-[#141435] font-plus-jakarta">
              Parcel {index + 1}
            </h3>
            {localParcels.length > 1 && !isLienRelease && (
              <button
                type="button"
                onClick={() => handleRemoveParcel(index)}
                className="text-red-500 hover:text-red-700 p-1 rounded-md transition-colors"
                title="Remove Parcel"
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            )}
          </div>

          <div className="flex flex-col gap-4">
            
            {freeze?.countyIndexes && freeze.countyIndexes.length > 0 && (
              <div className="flex flex-col gap-1 w-full">
                <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta">Associated Deed (Optional)</label>
                <select
                  className={`w-full px-3 py-2 rounded border border-[#D7DFEC] ${isLienRelease ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-[#141435]'} text-[15px] font-plus-jakarta focus:outline-none`}
                  value={parcel.countyIndexId || ''}
                  onChange={(e) => handleChange(index, 'countyIndexId', e.target.value ? Number(e.target.value) : null)}
                  disabled={isLienRelease}
                >
                  <option value="">None (Apply to all)</option>
                  {freeze.countyIndexes.map((ci: any, i: number) => {
                    let label = `Deed ${i + 1}`;
                    if (ci.recordingIndexes) {
                      const ri = ci.recordingIndexes;
                      const parts = [
                        ri.book ? `Bk: ${ri.book}` : '',
                        ri.page ? `Pg: ${ri.page}` : '',
                        ri.instrumentNumber ? `#${ri.instrumentNumber}` : ''
                      ].filter(Boolean).join(' ');
                      if (parts) label += ` - ${parts}`;
                    }
                    return <option key={ci.id} value={ci.id}>{label}</option>;
                  })}
                </select>
              </div>
            )}
            
            {parcelSchema.requiresParcelNumber && (
              <div className="flex flex-col gap-1 w-full">
                <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta">
                  Parcel Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 rounded border ${errors[index]?.parcelNumber ? 'border-red-500' : 'border-[#D7DFEC]'} ${isLienRelease ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-[#141435]'} text-[15px] font-plus-jakarta focus:outline-none`}
                  value={parcel.parcelNumber || ''}
                  onChange={(e) => handleChange(index, 'parcelNumber', e.target.value)}
                  disabled={isLienRelease}
                />
                {errors[index]?.parcelNumber && <span className="text-red-500 text-xs">{errors[index].parcelNumber}</span>}
              </div>
            )}

            {parcelSchema.parcelIndexes && parcelSchema.parcelIndexes.map((idx: any) => (
              <div key={idx.key} className="flex flex-col gap-1 w-full">
                <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta">
                  {idx.label} {idx.required !== false && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 rounded border ${errors[index]?.[`index_${idx.key}`] ? 'border-red-500' : 'border-[#D7DFEC]'} ${isLienRelease ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-[#141435]'} text-[15px] font-plus-jakarta focus:outline-none`}
                  value={getValue(index, idx.key)}
                  onChange={(e) => handleChange(index, idx.key, e.target.value)}
                  disabled={isLienRelease}
                />
                {errors[index]?.[`index_${idx.key}`] && (
                  <span className="text-red-500 text-xs mt-1">{errors[index][`index_${idx.key}`]}</span>
                )}
              </div>
            ))}

            {(parcelSchema.showAddress === true || (parcelSchema.showAddress !== false && parcelSchema.requiresAddress)) && (
              <div className="flex flex-col gap-1 w-full mt-2">
                <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta flex items-center gap-1 relative">
                  Property Address {parcelSchema.requiresAddress && <span className="text-red-500">*</span>}
                  <span className="relative group cursor-pointer ml-1">
                    <InformationCircleIcon className="w-4 h-4 text-[#0B4BB6]" />
                    <span className="absolute left-6 top-1/2 -translate-y-1/2 z-10 hidden group-hover:block bg-white border border-[#D7DFEC] rounded shadow-lg px-3 py-2 text-xs text-[#485D7D] w-64 font-normal font-plus-jakarta">
                      Enter the full address of the property (e.g., 123 Main St, City, ST 12345)
                    </span>
                  </span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    className={`w-full px-3 py-2 rounded border ${errors[index]?.street1 ? 'border-red-500' : 'border-[#D7DFEC]'} ${isLienRelease ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-[#141435]'} text-[15px] font-plus-jakarta focus:outline-none`}
                    value={parcel.street1 || ''}
                    onChange={(e) => {
                      handleChange(index, 'street1', e.target.value);
                      setActiveAddressIndex(index);
                      fetchAddressSuggestions(e.target.value);
                    }}
                    disabled={isLienRelease}
                    placeholder="Search or enter full address"
                  />
                  {addressSuggestions.length > 0 && activeAddressIndex === index && (
                    <ul className="absolute z-10 w-full bg-white border border-[#D7DFEC] rounded shadow-lg mt-1 max-h-60 overflow-auto">
                      {addressSuggestions.map((suggestion: any, sIdx: number) => (
                        <li
                          key={sIdx}
                          className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-[14px] text-[#141435]"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSelectSuggestion(suggestion, index);
                          }}
                        >
                          {suggestion.placePrediction.text.text}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {errors[index]?.street1 && <span className="text-red-500 text-xs">{errors[index].street1}</span>}
              </div>
            )}

            {(parcelSchema.showAbbreviatedLegalDescription === true || (parcelSchema.showAbbreviatedLegalDescription !== false && parcelSchema.requiresAbbreviatedLegalDescription)) && (
              <div className="flex flex-col gap-1 w-full mt-2">
                <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta">Abbreviated Legal Description {parcelSchema.requiresAbbreviatedLegalDescription && <span className="text-red-500">*</span>}</label>
                <textarea
                  className={`w-full px-3 py-2 rounded border ${errors[index]?.abbreviatedLegalDescription ? 'border-red-500' : 'border-[#D7DFEC]'} ${isLienRelease ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-[#141435]'} text-[15px] font-plus-jakarta focus:outline-none min-h-[60px]`}
                  value={parcel.abbreviatedLegalDescription || ''}
                  onChange={(e) => handleChange(index, 'abbreviatedLegalDescription', e.target.value)}
                  disabled={isLienRelease}
                  placeholder="e.g., Lot 4, Blk 2, Olympic Ridge, Vol 14, Pg 22"
                />
                {errors[index]?.abbreviatedLegalDescription && <span className="text-red-500 text-xs">{errors[index].abbreviatedLegalDescription}</span>}
              </div>
            )}

            {(parcelSchema.showLegalDescription === true || (parcelSchema.showLegalDescription !== false && parcelSchema.requiresLegalDescription)) && (
              <div className="flex flex-col gap-1 w-full mt-2">
                <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta">Legal Description {parcelSchema.requiresLegalDescription && <span className="text-red-500">*</span>}</label>
                <textarea
                  className={`w-full px-3 py-2 rounded border ${errors[index]?.legalDescription ? 'border-red-500' : 'border-[#D7DFEC]'} ${isLienRelease ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-[#141435]'} text-[15px] font-plus-jakarta focus:outline-none min-h-[100px]`}
                  value={parcel.legalDescription || ''}
                  onChange={(e) => handleChange(index, 'legalDescription', e.target.value)}
                  disabled={isLienRelease}
                />
                {errors[index]?.legalDescription && <span className="text-red-500 text-xs">{errors[index].legalDescription}</span>}
              </div>
            )}

          </div>
        </div>
      ))}

      {!isLienRelease && parcelSchema.allowMultipleParcels !== false && (
        <button
          type="button"
          onClick={handleAddParcel}
          className="flex items-center justify-center gap-2 w-full border-2 border-dashed border-[#0B4BB6] text-[#0B4BB6] font-bold text-[15px] font-plus-jakarta py-3 rounded-xl mt-2 hover:bg-blue-50 transition-colors"
        >
          <PlusIcon className="w-5 h-5" /> Add Another Parcel
        </button>
      )}

      <div className="flex justify-between mt-6 pt-6 border-t border-gray-200">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border-2 border-[#D7DFEC] text-[#485D7D] font-bold text-[15px] rounded-full hover:bg-gray-50 transition-colors"
          >
            Back
          </button>
        ) : <div />}
        <button
          type="button"
          onClick={handleSubmit}
          className={`px-8 py-2 bg-[#0B4BB6] text-white font-bold text-[15px] rounded-full hover:bg-[#23235a] transition-colors ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save & Continue'}
        </button>
      </div>
    </div>
  );
};

export default ParcelCollectionCard;
